import * as React from 'react';
import { connect } from 'react-redux';
import { uniq } from 'lodash';

import ImageUploader, { MEDIA_TYPES_IMAGES, MEDIA_TYPES_IMAGES_OR_VIDEOS } from 'components/ImageUploader';
import DeliverableMediaUploadPlaceholder from './placeholder/DeliverableMediaUploadPlaceholder';
import DeliverableAttachmentFeedbackModal from '../../deliverable-attachment-feedback-modal/DeliverableAttachmentFeedbackModal';

import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  filterAttachmentsByType
} from 'utils/deliverables';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentType
} from 'types';

import { createDeliverableAttachments } from '../../../reducers/deliverableMediaAttachments';

import type { DragEvent } from 'react';

type OwnProps = {
  size?: string,
  offerId: number,
  deliverable: Deliverable,
  attachmentType: DeliverableAttachmentType,
  ignoreMetricsCreation?: boolean, // eslint-disable-line react/no-unused-prop-types
  onUpdate?: () => void,
  onUploadModeTracking?: () => void
};

type StateProps = {
  uploadingAttachments: Array<DeliverableAttachment>,
  uploadProgressFiles: Record<string, number>,
  error: string
};

type DispatchProps = {
  dispatchCreateDeliverableAttachments: (
    offerId: number,
    deliverable: Deliverable,
    attachmentType: DeliverableAttachmentType,
    files: Array<File>,
    feedbackModal: React.ReactNode
  ) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isDragAndDropping: boolean
};

class DeliverableMediaUploader extends React.PureComponent<Props, State> {
  state = {
    isDragAndDropping: false
  };

  handleUploadFiles = (files: FileList) => {
    const {
      offerId,
      deliverable,
      dispatchCreateDeliverableAttachments,
      attachmentType,
      onUpdate,
      onUploadModeTracking
    } = this.props;

    const allowedFileTypes = attachmentType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS ? ['image'] : ['image', 'video'];
    const fileList = [...files].filter(file => allowedFileTypes.some(type => file.type.indexOf(type) >= 0));

    dispatchCreateDeliverableAttachments(
      offerId,
      deliverable,
      attachmentType,
      fileList,
      <DeliverableAttachmentFeedbackModal
        deliverableType={deliverable.deliverableType}
        attachmentType={attachmentType}
      />
    ).then(() => {
      if (onUpdate) {
        onUpdate();
      }

      if (onUploadModeTracking) {
        onUploadModeTracking();
      }
    });
  };

  handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleDragEnter = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: true
    });
  };

  handleDragLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: false
    });
  };

  handleDropFinished = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: false
    });

    this.handleUploadFiles(event.dataTransfer.files);
  };

  render(): React.ReactNode {
    const { attachmentType, uploadingAttachments, uploadProgressFiles, error, size } = this.props;
    const { isDragAndDropping } = this.state;
    const placeholderSize = size || 'normal';

    return (
      <React.Fragment>
        {
          uploadingAttachments.map(attachment => (
            <div
              key={attachment.id}
              className="deliverable-media-row__attachment"
            >
              <DeliverableMediaUploadPlaceholder
                size={placeholderSize}
                progress={uploadProgressFiles[attachment.id]}
                mode="uploading"
              />
            </div>
          ))
        }
        <div
          className="deliverable-media-row__attachment"
          onDragEnter={this.handleDragEnter}
          onDragLeave={this.handleDragLeave}
          onDragOver={this.handleDragOver}
          onDrop={this.handleDropFinished}
        >
          <ImageUploader
            key={`uploader-${uploadingAttachments.length > 0 ? 'uploading' : 'ready'}`} // force to rerender to upload the same file again in Chrome COLLAB-3929
            multiple
            mediaTypes={attachmentType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS ? MEDIA_TYPES_IMAGES : MEDIA_TYPES_IMAGES_OR_VIDEOS}
            onUpload={this.handleUploadFiles}
          >
            <DeliverableMediaUploadPlaceholder
              size={placeholderSize}
              mode={isDragAndDropping ? 'drop' : 'create'}
            />
          </ImageUploader>
        </div>
        {
          error &&
            <div className="deliverable-media-row__error">
              {error}
            </div>
        }
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ deliverableMediaAttachments, uploadProgressReducer }, props: OwnProps): StateProps => {
  const uploadingAttachments = deliverableMediaAttachments.uploadingAttachmentsByDeliverableId[props.deliverable.id] || [];
  const errors = (deliverableMediaAttachments.errorsByDeliverableId[props.deliverable.id] || [])
    .filter(item => item.attachmentType === props.attachmentType);

  return {
    uploadingAttachments: filterAttachmentsByType(uploadingAttachments, props.attachmentType),
    error: uniq(errors.map(item => item.error)).join(' '),
    uploadProgressFiles: uploadProgressReducer.files
  };
};

const mapDispatchToProps = (dispatch, { ignoreMetricsCreation }: OwnProps): DispatchProps => ({
  dispatchCreateDeliverableAttachments: (offerId, deliverable, attachmentType, files, feedbackModal): Promise<void> =>
    dispatch(createDeliverableAttachments(offerId, deliverable, attachmentType, files, feedbackModal, ignoreMetricsCreation))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableMediaUploader);
